import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { UomSelect } from "@/components/UomSelect";
import { EmptyState } from "@/components/EmptyState";
import { getErrorMessage } from "@/lib/errors";

const DAY_MS = 24 * 60 * 60 * 1000;
const dayCount = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / DAY_MS) + 1;

/**
 * Planning-only entry: scope, UoM, planned start and planned end for every construction
 * activity in one table. Saves without creating any progress (actual) entry.
 */
export function PlanSetup({ projectId, items, canEdit, onSaved }) {
  const [edits, setEdits] = useState({});
  const [rebuild, setRebuild] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkStart, setBulkStart] = useState("");
  const [bulkEnd, setBulkEnd] = useState("");

  const original = (it) => ({
    quantity: it.quantity ?? "",
    uom: it.uom || "",
    start: it.construction?.scheduled_start_date || "",
    end: it.construction?.scheduled_end_date || "",
  });
  const current = (it) => ({ ...original(it), ...(edits[it.id] || {}) });
  const setField = (it, field, value) =>
    setEdits((e) => {
      const next = { ...(e[it.id] || {}), [field]: value };
      if (next[field] === original(it)[field]) delete next[field];
      const out = { ...e };
      if (Object.keys(next).length) out[it.id] = next; else delete out[it.id];
      return out;
    });

  const leaves = useMemo(() => items.filter((i) => !i.is_group), [items]);
  const changedIds = Object.keys(edits);
  const problem = (it) => {
    const c = current(it);
    if (c.start && c.end && c.end < c.start) return "End is before start";
    if (c.quantity !== "" && Number(c.quantity) < 0) return "Scope can't be negative";
    return null;
  };
  const hasProblem = leaves.some((it) => problem(it));

  const fillBlankDates = () => {
    if (!bulkStart && !bulkEnd) return;
    setEdits((e) => {
      const out = { ...e };
      leaves.forEach((it) => {
        const c = { ...original(it), ...(out[it.id] || {}) };
        const next = { ...(out[it.id] || {}) };
        if (bulkStart && !c.start) next.start = bulkStart;
        if (bulkEnd && !c.end) next.end = bulkEnd;
        if (Object.keys(next).length) out[it.id] = next;
      });
      return out;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // send only the activities that were edited, with their full current values
      const rows = leaves.filter((it) => edits[it.id]).map((it) => {
        const c = current(it);
        return {
          wbs_item_id: it.id,
          quantity: c.quantity === "" ? null : Number(c.quantity),
          uom: c.uom || null,
          scheduled_start_date: c.start || null,
          scheduled_end_date: c.end || null,
        };
      });
      const { data } = await api.put(`/projects/${projectId}/plan-setup`, { rows, replan_changed: rebuild });
      toast.success(`Saved ${data.activities_saved} activit${data.activities_saved === 1 ? "y" : "ies"} · plan created ${data.plans_created}, rebuilt ${data.plans_rebuilt}`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      setEdits({});
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (leaves.length === 0) {
    return <EmptyState title="No construction activities" description="Add construction activities in the WBS first, then plan them here." testId="plan-setup-empty" />;
  }

  return (
    <div className="space-y-3" data-testid="plan-setup">
      <div className="rounded-xl border border-border bg-card p-3 text-sm text-muted-foreground">
        Enter the plan for each activity: <b className="text-foreground">scope</b>, <b className="text-foreground">UoM</b>, <b className="text-foreground">planned start</b> and <b className="text-foreground">planned end</b>.
        This is planning only — no progress is recorded. When you save, the daily plan is built automatically from scope and dates.
        {!canEdit && <span className="ml-1 font-semibold text-amber-600 dark:text-amber-400">Only the project manager can change these.</span>}
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Fill blank start dates</p>
            <Input type="date" className="h-8 w-40" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} data-testid="plan-setup-bulk-start" />
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Fill blank end dates</p>
            <Input type="date" className="h-8 w-40" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} data-testid="plan-setup-bulk-end" />
          </div>
          <Button size="sm" variant="outline" onClick={fillBlankDates} disabled={!bulkStart && !bulkEnd} data-testid="plan-setup-bulk-apply">Apply to blank rows</Button>
          <label className="ml-auto flex items-center gap-2 text-xs text-foreground">
            <Checkbox checked={rebuild} onCheckedChange={(v) => setRebuild(!!v)} data-testid="plan-setup-rebuild" />
            Rebuild the daily plan for changed activities
          </label>
          <Button size="sm" onClick={save} disabled={saving || changedIds.length === 0 || hasProblem} data-testid="plan-setup-save">
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
            Save planning ({changedIds.length})
          </Button>
        </div>
      )}
      {canEdit && rebuild && (
        <p className="text-[11px] text-muted-foreground">Rebuilding replaces any hand-typed daily plan quantities on the activities you change. Untouched activities are never rebuilt.</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-card">
            <tr>
              {["Code", "Activity", "Scope", "UoM", "Planned start", "Planned end", "Days", "Ready?"].map((h) => (
                <th key={h} className="whitespace-nowrap border-b border-border px-2 py-2 text-left text-xs font-semibold text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              if (it.is_group) {
                return (
                  <tr key={it.id} className="bg-muted/40">
                    <td colSpan={8} className="border-b border-border px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-foreground">{it.name}</td>
                  </tr>
                );
              }
              const c = current(it);
              const bad = problem(it);
              const missing = [];
              if (c.quantity === "" || Number(c.quantity) <= 0) missing.push("scope");
              if (!c.start) missing.push("start");
              if (!c.end) missing.push("end");
              const days = c.start && c.end && c.end >= c.start ? dayCount(c.start, c.end) : null;
              const dirty = !!edits[it.id];
              return (
                <tr key={it.id} className={dirty ? "bg-gold-500/5" : ""} data-testid={`plan-setup-row-${it.id}`}>
                  <td className="border-b border-border px-2 py-1.5 font-mono text-xs">{it.code}</td>
                  <td className="border-b border-border px-2 py-1.5 text-xs text-foreground">{it.name}</td>
                  <td className="border-b border-border px-1 py-1">
                    <Input type="number" min={0} step="any" disabled={!canEdit} className="h-8 w-28 px-2 text-xs" value={c.quantity}
                      onChange={(e) => setField(it, "quantity", e.target.value)} data-testid={`plan-setup-qty-${it.id}`} />
                  </td>
                  <td className="border-b border-border px-1 py-1">
                    {canEdit
                      ? <UomSelect value={c.uom} onChange={(v) => setField(it, "uom", v)} className="h-8 w-28 text-xs" testId={`plan-setup-uom-${it.id}`} />
                      : <span className="px-2 text-xs">{c.uom || "—"}</span>}
                  </td>
                  <td className="border-b border-border px-1 py-1">
                    <Input type="date" disabled={!canEdit} className="h-8 w-36 px-2 text-xs" value={c.start}
                      onChange={(e) => setField(it, "start", e.target.value)} data-testid={`plan-setup-start-${it.id}`} />
                  </td>
                  <td className="border-b border-border px-1 py-1">
                    <Input type="date" disabled={!canEdit} className="h-8 w-36 px-2 text-xs" value={c.end}
                      onChange={(e) => setField(it, "end", e.target.value)} data-testid={`plan-setup-end-${it.id}`} />
                  </td>
                  <td className="border-b border-border px-2 py-1.5 font-mono text-xs text-muted-foreground">{days ?? "—"}</td>
                  <td className="border-b border-border px-2 py-1.5 text-[11px]">
                    {bad
                      ? <span className="font-semibold text-red-600 dark:text-red-400">{bad}</span>
                      : missing.length
                        ? <span className="text-amber-600 dark:text-amber-400">Needs {missing.join(", ")}</span>
                        : <span className="font-semibold text-emerald-600 dark:text-emerald-400">Ready</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
